/**
 * Financing quiz — "Do you need an unsecured business loan?"
 *
 * Verified paths through the decision tree:
 *   Q1 No  → Outcome A (SBA microloan / credit card)
 *   Q1 Yes → Q2 Yes → Outcome B (secured loan)
 *   Q1 Yes → Q2 No  → Q3 "One-off investment"    → Outcome C (term loan)
 *   Q1 Yes → Q2 No  → Q3 "Ongoing cash flow gaps" → Outcome D (line of credit)
 */
"use client";

import { AnimatePresence } from "framer-motion";
import { useLocale } from "next-intl";
import { useReducer } from "react";

import { localizeCurrencyString } from "@/lib/localize-currency";
import { quizConfig } from "@/lib/quiz-config";
import type {
  OutcomeId,
  QuestionId,
  QuizAction,
  QuizState,
} from "@/lib/quiz-types";

import { IntroScreen } from "./IntroScreen";
import { OutcomeScreen } from "./OutcomeScreen";
import { QuestionScreen } from "./QuestionScreen";

const initialState: QuizState = {
  screen: { type: "intro" },
  answers: {},
  history: [],
};

function quizReducer(state: QuizState, action: QuizAction): QuizState {
  switch (action.type) {
    case "START":
      // TODO: track event quiz_started
      return {
        ...state,
        screen: { type: "question", id: "q1" },
        history: [...state.history, state.screen],
      };

    case "ANSWER": {
      const question = quizConfig.questions.find(
        (q) => q.id === action.questionId
      );
      if (!question) return state;
      const option = question.options.find((o) => o.id === action.optionId);
      if (!option) return state;

      const next = option.next;
      const nextScreen =
        next.type === "question"
          ? ({ type: "question", id: next.id } as const)
          : ({ type: "outcome", id: next.id } as const);

      // TODO: track event outcome_reached (when nextScreen.type === "outcome")

      return {
        screen: nextScreen,
        answers: { ...state.answers, [action.questionId]: action.optionId },
        history: [...state.history, state.screen],
      };
    }

    case "BACK": {
      if (state.history.length === 0) return state;
      const history = state.history.slice(0, -1);
      const screen = state.history[state.history.length - 1] as NonNullable<
        QuizState["history"][number]
      >;
      return { ...state, screen, history };
    }

    case "RESTART":
      return initialState;

    default:
      return state;
  }
}

export function Quiz() {
  const locale = useLocale();
  const [state, dispatch] = useReducer(quizReducer, initialState);
  const { screen, answers, history } = state;

  const screenKey =
    screen.type === "intro"
      ? "intro"
      : screen.type === "question"
        ? `question-${screen.id}`
        : `outcome-${screen.id}`;

  return (
    <div className="not-prose rounded-ui-large-xxl my-10 bg-black p-4 text-white lg:p-8">
      <div className="mx-auto max-w-[640px]">
        <AnimatePresence mode="wait">
          {screen.type === "intro" && (
            <IntroScreen
              key={screenKey}
              onStart={() => dispatch({ type: "START" })}
            />
          )}

          {screen.type === "question" &&
            (() => {
              const question = quizConfig.questions.find(
                (q) =>
                  q.id === (screen as { type: "question"; id: QuestionId }).id
              );
              if (!question) return null;
              const localizedQuestion = {
                ...question,
                prompt: localizeCurrencyString(question.prompt, locale),
              };
              const questionScreen = screen as {
                type: "question";
                id: QuestionId;
              };
              return (
                <QuestionScreen
                  key={screenKey}
                  question={localizedQuestion}
                  totalQuestions={quizConfig.questions.length}
                  selectedOptionId={answers[question.id]}
                  canGoBack={history.length > 0 && question.number > 1}
                  onAnswer={(optionId) =>
                    dispatch({
                      type: "ANSWER",
                      questionId: questionScreen.id,
                      optionId,
                    })
                  }
                  onBack={() => dispatch({ type: "BACK" })}
                />
              );
            })()}

          {screen.type === "outcome" &&
            (() => {
              const outcomeScreen = screen as {
                type: "outcome";
                id: OutcomeId;
              };
              const outcome = quizConfig.outcomes[outcomeScreen.id];
              if (!outcome) return null;
              return (
                <OutcomeScreen
                  key={screenKey}
                  outcome={outcome}
                  onRestart={() => dispatch({ type: "RESTART" })}
                />
              );
            })()}
        </AnimatePresence>
      </div>
    </div>
  );
}
